import i18n from '@/plugins/vue-i18n';
import { useAlertsStore } from '@/stores/alerts';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useClusteringStore } from '@/stores/clustering';
import to from 'await-to-js';
import axios from 'axios';
import { get, groupBy, isEmpty, size } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import handleErrorMessages from '@/stores/utils/validation';

export const useScenarioProductsStore = defineStore('scenarioProducts', () => {
  const scenariosStore = useScenariosStore();
  const snackbarStore = useSnackbarStore();
  const alertsStore = useAlertsStore();
  const clusteringStore = useClusteringStore();
  // States
  const scenarioProducts = ref([]);
  const overviewProductOrder = ref([]);
  const loading = shallowRef(false);

  // Getters
  const hasNewProducts = computed(() => {
    return scenarioProducts.value.some(p => p.isNewProduct);
  });

  const scenarioProductPallets = computed(() => {
    return groupBy(scenarioProducts.value, 'palletContent');
  });

  const consumeOveriewProductOrder = computed(() => {
    const value = [...overviewProductOrder.value];
    // This should only be usable once - unset after retrieval.
    // This is used to order products on detail view when navigating - it shouldn't preserve past that.
    overviewProductOrder.value = [];
    return value;
  });

  // Actions
  const getScenarioProductStateByProductKey = productKey => {
    const product = scenarioProducts.value.find(p => p.productKey === productKey);
    return product;
  };

  async function fetchScenarioProducts({ params, options = {} } = {}) {
    const scenarioId = get(params, 'scenarioId', scenariosStore.selectedScenario._id);
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/scenario-products/scenario/${scenarioId}`, { params })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    if (options.shouldCommit) {
      scenarioProducts.value = response.data;
    }
    return response.data;
  }

  async function processCSV({ fileId, mappings, delimiter, service = 'scenario-products' }) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const body = { mappings, scenarioId, delimiter, scenarioProductsCsv: true };
    loading.value = true;
    const [err, res] = await to(axios.post(`/api/csv/${service}/process/${fileId}`, body));
    loading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);

      return Promise.reject(message);
    }
    const { messages, tableRows, additionalInformation } = res.data;

    if (size(messages)) {
      alertsStore.showMessages(messages);
    }

    return { tableRows, additionalInformation };
  }

  async function resetProductData(dataToReset) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const [err, data] = await to(
      axios.get(`/api/scenario-products/scenario/${scenarioId}/reset-attributes`, {
        params: { dataToReset },
      })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    return data;
  }

  async function saveScenarioDetails(updates) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const [err, data] = await to(
      axios.patch(`/api/scenario-products/scenario/${scenarioId}/update-product-details`, {
        params: { updates },
      })
    );

    loading.value = false;

    if (err) {
      handleErrorMessages({
        response: get(err, 'response'),
        options: { catchJoi: true },
      });

      return Promise.reject(err.message);
    }

    return data;
  }

  async function saveScenarioProducts(productsData) {
    loading.value = true;
    const [err, data] = await to(axios.patch('/api/scenario-products', productsData));
    loading.value = false;
    if (err) {
      handleErrorMessages({
        response: get(err, 'response'),
        options: { catchJoi: true },
      });
      return Promise.reject(err.message);
    }

    return data;
  }

  async function updateScenarioProductRegionClassifications({ attributeId, attributeValues }) {
    loading.value = true;
    const scenarioId = scenariosStore.selectedScenario._id;
    const [err, data] = await to(
      axios.patch(`/api/scenario-products/scenario/${scenarioId}/region-classifications`, {
        attributeId,
        attributeValues,
      })
    );
    loading.value = false;
    if (err) return false;
    return data;
  }

  async function fetchScenarioUniqueAttributeValues({ scenarioId }) {
    loading.value = true;
    const [err, result] = await to(
      axios.get(`/api/scenario-products/scenario/${scenarioId}/uniqueAttributeValues`)
    );
    loading.value = false;
    if (err) return false;
    return result.data;
  }

  async function fetchScenarioProductsWithClusters() {
    // This pulls the data for the product modelling page
    // This will eventually pull all products so gets own method to be performant
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, result] = await to(
      axios.get(`/api/scenario-products/scenario/${scenarioId}/clustering-products/modelling`)
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    return result.data;
  }

  // A method that gets the information about the matching and non-matching products
  // between source and current scenario when copying modelling decisions
  async function getMatchingProductsInfo(sourceScenarioId, filteredProductDisplayKeys = []) {
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(
      axios.post(
        `/api/scenario-products/scenario/${scenarioId}/matching-products-info?sourceScenarioId=${sourceScenarioId}`,
        { filteredProductDisplayKeys }
      )
    );
    loading.value = false;
    handleErrorMessages({ response });
    if (err) throw new Error(get(err, 'message'));
    return response.data;
  }

  async function updateClusteredScenarioProducts(updates) {
    // You don't always have clusteredUpdates, return if not.
    if (isEmpty(updates)) return Promise.resolve();

    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;
    const clusterSchemeId = clusteringStore.selectedClusterScheme._id;

    loading.value = true;
    const [err, result] = await to(
      axios.patch(
        `/api/scenario-products/scenario/${scenarioId}/cluster-scheme/${clusterSchemeId}`,
        { updates }
      )
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    return result.data;
  }

  async function updateScenarioSwapsAttributes(updates) {
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;

    loading.value = true;
    const [err, result] = await to(
      axios.patch(`/api/scenario-products/scenario/${scenarioId}/swaps-attributes`, { updates })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    return result.data;
  }

  async function uploadAttributesFromSource({ attributeNames, optionalSchemaFields = [] }) {
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;

    loading.value = true;
    const [err, result] = await to(
      axios.post(`/api/scenario-products/scenario/${scenarioId}/upload-attributes-source`, {
        attributeNames,
        optionalSchemaFields,
      })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    return result.data;
  }

  async function uploadAttributesFromScenario({
    sourceScenarioId = null,
    templateId,
    optionalSchemaFields = [],
  }) {
    const scenarioStore = useScenariosStore();
    const targetScenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;

    const [err, result] = await to(
      axios.post(`/api/scenario-products/scenario/${targetScenarioId}/upload-attributes-scenario`, {
        sourceScenarioId,
        templateId,
        optionalSchemaFields,
      })
    );
    loading.value = false;

    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);

      return Promise.reject(message);
    }

    return result.data;
  }

  async function processAttributes({
    productKeys,
    source,
    attributeSource,
    mappings,
    attributeNames,
    sourceScenarioId,
    templateId,
  }) {
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, res] = await to(
      axios.post(`/api/scenario-products/scenario/${scenarioId}/process-attributes`, {
        productKeys,
        source,
        attributeSource,
        mappings,
        attributeNames,
        sourceScenarioId,
        templateId,
      })
    );
    loading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));

      snackbarStore.showError(message);

      return Promise.reject(message);
    }
    const { messages, tableRows, additionalInformation } = res.data;

    if (size(messages)) {
      alertsStore.showMessages(messages);
    }

    return { tableRows, additionalInformation };
  }

  async function copyModellingDecisions(payload) {
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(
      axios.post(`/api/scenario-products/scenario/${scenarioId}/apply-modelling`, payload)
    );
    loading.value = false;

    handleErrorMessages({ response });
    if (err) throw new Error(get(err, 'message'));
    return response;
  }

  async function runSetupProductModelling(payload) {
    const scenarioStore = useScenariosStore();
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(
      axios.post(`/api/scenario-products/scenario/${scenarioId}/setup-modelling`, payload)
    );
    loading.value = false;
    scenariosStore.updateScenarioJobAndStatus();
    handleErrorMessages({ response });
    if (get(response, 'data.messageType') === 'JobValidation') {
      return get(response, 'data.data');
    }
    if (err) throw new Error(get(err, 'message'));
  }

  function getAssociatedPallets({ productKeyDisplay }) {
    return get(scenarioProductPallets.value, productKeyDisplay, []);
  }

  function setScenarioProducts(newValue) {
    scenarioProducts.value = newValue;
  }

  async function uploadAttributesFromFurniture() {
    const scenarioStore = useScenariosStore();
    const targetScenarioId = scenarioStore.selectedScenario._id;
    this.loading = true;

    const [err, result] = await to(
      axios.get(`/api/scenario-products/scenario/${targetScenarioId}/upload-attributes-furniture`)
    );
    this.loading = false;

    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);

      return Promise.reject(message);
    }

    return result.data;
  }

  return {
    // State
    scenarioProducts,
    overviewProductOrder,
    consumeOveriewProductOrder,
    loading,
    // Getters
    hasNewProducts,
    scenarioProductPallets,
    // Actions
    getScenarioProductStateByProductKey,
    fetchScenarioProducts,
    processCSV,
    resetProductData,
    saveScenarioProducts,
    saveScenarioDetails,
    updateScenarioProductRegionClassifications,
    fetchScenarioUniqueAttributeValues,
    fetchScenarioProductsWithClusters,
    getMatchingProductsInfo,
    updateClusteredScenarioProducts,
    updateScenarioSwapsAttributes,
    uploadAttributesFromSource,
    uploadAttributesFromScenario,
    processAttributes,
    copyModellingDecisions,
    runSetupProductModelling,
    getAssociatedPallets,
    setScenarioProducts,
    uploadAttributesFromFurniture,
  };
});
